import type { ReactNode } from 'react';
import { useMemo, useEffect } from 'react';

import debounce from 'lodash-es/debounce';
import { ArrowLeftIcon } from 'lucide-react';
import {
  useFormContext,
  Control,
  FieldValues,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';

interface FilterDropdownFormProps<T extends FieldValues = FieldValues> {
  control: Control<T>;
  setValue: UseFormSetValue<T>;
  watch: UseFormWatch<T>;
}

interface FilterDropdownProps<T extends FieldValues = FieldValues> {
  title: string;
  children: ReactNode | ((formProps: FilterDropdownFormProps<T>) => ReactNode);
  onApply?: () => void;
  onClose?: (source?: 'back' | 'outside') => void;
  onSelectAll?: () => void;
  onSelectNone?: () => void;
  showAllNoneButtons?: boolean;
  compactPadding?: boolean;
  // For external control
  control?: Control<T>;
  setValue?: UseFormSetValue<T>;
  watch?: UseFormWatch<T>;
  showBackButton?: boolean;
  headerClass?: string;
}

export function FilterDropdownWrapper<T extends FieldValues = FieldValues>({
  title,
  children,
  onApply,
  onClose,
  onSelectAll,
  onSelectNone,
  showAllNoneButtons = true,
  control: externalControl,
  setValue: externalSetValue,
  watch: externalWatch,
  showBackButton = true,
  headerClass,
}: FilterDropdownProps<T>) {
  const { t } = useTranslation();
  // Try to get form methods from context first
  const formContext = useFormContext<T>();

  const control = externalControl || formContext?.control;
  const setValue = externalSetValue || formContext?.setValue;
  const watch = externalWatch || formContext?.watch;

  // Create debounced version of onApply
  const debouncedApply = useMemo(() => (onApply ? debounce(onApply, 200) : undefined), [onApply]);

  // Auto-apply with debouncing when form values change
  useEffect(() => {
    if (!debouncedApply || !watch) {
      return;
    }

    const subscription = watch(() => {
      debouncedApply();
    });

    return () => {
      if (subscription && typeof subscription.unsubscribe === 'function') {
        subscription.unsubscribe();
      }
      debouncedApply.cancel();
    };
  }, [watch, debouncedApply]);

  const renderChildren = () => {
    if (typeof children === 'function') {
      if (!control || !setValue || !watch) {
        console.warn(
          'FilterDropdown: Form methods not available. Wrap with FormProvider or pass form props.'
        );
        return null;
      }
      return children({ control, setValue, watch });
    }
    return children;
  };

  return (
    <div className="w-full">
      <Card className="w-full gap-2 rounded-md border py-0 shadow-none">
        <CardHeader className={cn('pt-2 pr-3 pl-2.5', headerClass)}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              {onClose && showBackButton && (
                <Button onClick={() => onClose('back')} size="icon-xss" variant="ghost-neutral">
                  <ArrowLeftIcon size={20} className="text-sidebar-foreground" />
                </Button>
              )}
              <CardTitle className="text-sm leading-6 font-semibold">{title}</CardTitle>
            </div>
            {showAllNoneButtons && (onSelectAll || onSelectNone) && (
              <div className="flex items-center gap-2 pr-1 text-sm font-medium text-primary">
                {onSelectNone && <button onClick={onSelectNone}>{t('common.clear')}</button>}
                {onSelectAll && onSelectNone && <span>·</span>}
                {onSelectAll && <button onClick={onSelectAll}>{t('common.all')}</button>}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="px-0">{renderChildren()}</CardContent>
      </Card>
    </div>
  );
}
